import { useEffect, useState, useCallback } from 'react'
import {
  apiLoanList, apiLoanApply, apiLoanRepay,
  apiDepositList, apiDepositOpen, apiDepositClose,
  apiFinanceSummary,
} from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface Header {
  CASH: number; CREDIT_GRADE: string; TOTAL_ASSETS: number
  LOAN_LIMIT: number; LOAN_BALANCE: number; LOAN_REMAIN: number; RATE_BPS: number
}
interface FinHeader { CASH: number; CREDIT_GRADE: string; CREDIT_SCORE: number; DEFICIT_STREAK: number; SURPLUS_STREAK: number; LAST_SETTLED_MONTH: number }
interface CreditHist { MONTH_NO: number; CREDIT_SCORE: number; CREDIT_GRADE: string }
interface LoanRow {
  LOAN_CODE: number; PRINCIPAL: number; BALANCE: number
  INTEREST_RATE_BPS: number; START_ROUND: number; START_SEASON: number; REG_DT: string
}
interface DepositRow {
  DEPOSIT_CODE: number; PRINCIPAL: number; BALANCE: number
  INTEREST_RATE_BPS: number; START_ROUND: number; START_SEASON: number; REG_DT: string
}

interface Props { auth: AuthState; clubCode: number; seasonNo: number; nonce?: number }

// ── Phase 9: 신용등급 표 (WFM_CONFIG LOAN_RATE_*/LOAN_LIMIT_* 기준) ──
const GRADE_TABLE: { g: string, rate: number | null, limit: number }[] = [
  { g: 'AAA', rate: 6.0,  limit: 200 },
  { g: 'AA',  rate: 7.0,  limit: 150 },
  { g: 'A',   rate: 8.0,  limit: 120 },
  { g: 'BBB', rate: 9.0,  limit: 100 },
  { g: 'BB',  rate: 11.0, limit: 70 },
  { g: 'B',   rate: 13.0, limit: 50 },
  { g: 'CCC', rate: 15.0, limit: 30 },
  { g: 'CC',  rate: 15.0, limit: 15 },
  { g: 'D',   rate: null, limit: 0 },
]
const DEPOSIT_RATE_PCT = 1.0

export default function Banking({ auth, clubCode, seasonNo, nonce }: Props) {
  const [header, setHeader]     = useState<Header | null>(null)
  const [fin, setFin]           = useState<FinHeader | null>(null)
  const [loans, setLoans]       = useState<LoanRow[]>([])
  const [deposits, setDeposits] = useState<DepositRow[]>([])
  const [actionMsg, setActionMsg] = useState<{text: string, ok: boolean} | null>(null)
  const [loading, setLoading]   = useState(true)
  const [modal, setModal]       = useState<ModalSpec | null>(null)
  const [hist, setHist]         = useState<CreditHist[]>([])

  const reload = useCallback(async () => {
    setLoading(true)
    const [l, d, s] = await Promise.all([
      apiLoanList(auth.token, clubCode),
      apiDepositList(auth.token, clubCode),
      apiFinanceSummary(auth.token, clubCode, seasonNo),
    ])
    if (l.resultCode === '00') {
      try { const obj = JSON.parse(l.resultData); setHeader(obj.header); setLoans(obj.loans ?? []) } catch {}
    }
    if (d.resultCode === '00') {
      try { const obj = JSON.parse(d.resultData); setDeposits(obj.rows ?? []) } catch {}
    }
    if (s.resultCode === '00') {
      try { const obj = JSON.parse(s.resultData); setFin(obj.header ?? null); setHist(obj.creditHistory ?? []) } catch {}
    }
    setLoading(false)
  }, [auth.token, clubCode, seasonNo])

  useEffect(() => { reload() }, [reload, nonce])

  async function run(p: Promise<{ resultCode: string, resultMessage: string }>) {
    const r = await p
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    setModal(null)
    if (r.resultCode === '00') reload()
  }

  function openLoanApply() {
    if (!header) return
    setModal({
      title: '신규 대출', label: '대출 금액', confirmLabel: '대출 실행', kind: 'buy',
      defaultAmount: header.LOAN_REMAIN, maxAmount: header.LOAN_REMAIN,
      projection: (amt) => `연 이자 약 ₩${Math.round(amt * header.RATE_BPS / 10000).toLocaleString()} (연 ${(header.RATE_BPS / 100).toFixed(1)}%)`,
      onConfirm: (amt) => run(apiLoanApply(auth.token, amt)),
    })
  }
  function openLoanRepay(l: LoanRow) {
    setModal({
      title: `대출 #${l.LOAN_CODE} 상환`, label: '상환 금액', confirmLabel: '상환', kind: 'cancel',
      defaultAmount: l.BALANCE, maxAmount: l.BALANCE,
      projection: (amt) => `상환 후 잔액 ₩${Math.max(0, l.BALANCE - amt).toLocaleString()}`,
      onConfirm: (amt) => run(apiLoanRepay(auth.token, l.LOAN_CODE, amt)),
    })
  }
  function openDepositOpen() {
    setModal({
      title: '신규 예금', label: '예치 금액', confirmLabel: '예금 가입', kind: 'buy',
      defaultAmount: header ? Math.min(header.CASH, 1000000000) : 1000000000,
      maxAmount: header ? header.CASH : undefined,
      projection: (amt) => `예상 연 이자 약 ₩${Math.round(amt * DEPOSIT_RATE_PCT / 100).toLocaleString()} (연 ${DEPOSIT_RATE_PCT.toFixed(1)}%)`,
      onConfirm: (amt) => run(apiDepositOpen(auth.token, amt)),
    })
  }
  function openDepositClose(dp: DepositRow) {
    setModal({
      title: `예금 #${dp.DEPOSIT_CODE} 해지`, label: '해지 잔액 (이자 포함)', confirmLabel: '해지', kind: 'cancel',
      defaultAmount: dp.BALANCE, maxAmount: dp.BALANCE, fixed: true,
      projection: () => `해지 시 ₩${dp.BALANCE.toLocaleString()} 입금`,
      onConfirm: () => run(apiDepositClose(auth.token, dp.DEPOSIT_CODE)),
    })
  }

  if (loading) return <div className="page-pad">은행 정보 불러오는 중…</div>

  const curIdx = header ? GRADE_TABLE.findIndex(x => x.g === header.CREDIT_GRADE) : -1
  const deficit = fin?.DEFICIT_STREAK ?? 0
  const surplus = fin?.SURPLUS_STREAK ?? 0
  const score   = fin?.CREDIT_SCORE ?? 0
  const scorePct = Math.max(0, Math.min(100, score / 10))   // 0~1000 → 0~100%

  return (
    <div className="page-pad banking-page">
      <h2>은행</h2>

      {header && (
        <section className="card banking-header">
          <div><label>현금</label><strong>₩ {header.CASH.toLocaleString()}</strong></div>
          <div><label>총자산 추정</label><strong>₩ {header.TOTAL_ASSETS.toLocaleString()}</strong></div>
          <div><label>신용등급</label><strong className={`grade-${header.CREDIT_GRADE}`}>{header.CREDIT_GRADE}</strong></div>
          <div><label>대출 사용 / 한도</label><strong>₩ {header.LOAN_BALANCE.toLocaleString()} / {header.LOAN_LIMIT.toLocaleString()}</strong></div>
          <div><label>잔여 한도</label><strong className="profit">₩ {header.LOAN_REMAIN.toLocaleString()}</strong></div>
          <div><label>대출 이율</label><strong>연 {(header.RATE_BPS / 100).toFixed(1)}%</strong></div>
        </section>
      )}

      {actionMsg && <p className={actionMsg.ok ? 'msg ok' : 'msg'}>{actionMsg.text}</p>}

      {/* ── Phase 9: 신용등급 상세 ── */}
      {header && (
        <section className="card credit-detail">
          <h3>신용등급 상세</h3>
          <div className="credit-top">
            <div className={`credit-badge grade-${header.CREDIT_GRADE}`}>{header.CREDIT_GRADE}</div>
            <div className="credit-meta">
              <div className="credit-score-row">
                <span>신용 점수</span>
                <span className="credit-score-val"><b>{score}</b> / 1000</span>
                <span className="credit-score-bar"><i style={{ width: `${scorePct}%` }} /></span>
              </div>
              <div>현재 등급 적용 — 대출 이율 <b>연 {(header.RATE_BPS / 100).toFixed(1)}%</b> · 한도 총자산의 <b>{GRADE_TABLE[curIdx]?.limit ?? '-'}%</b></div>
              <div className="credit-streaks">
                <span className={surplus > 0 ? 'up' : 'muted'}>흑자 누적 {surplus}개월</span>
                <span className={deficit > 0 ? 'down' : 'muted'}>적자 누적 {deficit}개월</span>
              </div>
              <div className="muted">신용 점수는 매월 결산에서 <b>현금 유동성 · 부채비율 · 흑자/적자 누적</b>으로 재산정되어 등급이 <b>상·하향</b>됩니다(점진 반영). 월별 손익은 ‘재정’ 탭 참조.</div>
            </div>
          </div>
          <div className="credit-trend">
            <div className="credit-trend-head">신용 점수 추이</div>
            <CreditTrendSvg hist={hist} />
          </div>
          <table className="data-table grade-table">
            <thead><tr><th>등급</th><th>대출 이율</th><th>한도(총자산 대비)</th></tr></thead>
            <tbody>
              {GRADE_TABLE.map(row => (
                <tr key={row.g} className={row.g === header.CREDIT_GRADE ? 'cur' : ''}>
                  <td><span className={`grade-${row.g}`}>{row.g}</span></td>
                  <td>{row.rate != null ? `연 ${row.rate.toFixed(1)}%` : '대출 불가'}</td>
                  <td>{row.limit}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">예금 이율 연 {DEPOSIT_RATE_PCT.toFixed(1)}% (자유예금)</p>
        </section>
      )}

      <section className="card">
        <div className="banking-section-head">
          <h3>대출</h3>
          <button className="btn-buy" onClick={openLoanApply} disabled={!header || header.LOAN_REMAIN <= 0}>신규 대출</button>
        </div>
        <table className="data-table">
          <thead><tr><th>코드</th><th>원금</th><th>잔액</th><th>연이율</th><th>시작</th><th></th></tr></thead>
          <tbody>
            {loans.map(l => (
              <tr key={l.LOAN_CODE}>
                <td>#{l.LOAN_CODE}</td>
                <td className="num">₩ {l.PRINCIPAL.toLocaleString()}</td>
                <td className="num">₩ {l.BALANCE.toLocaleString()}</td>
                <td>{(l.INTEREST_RATE_BPS / 100).toFixed(1)}%</td>
                <td>S{l.START_SEASON} R{l.START_ROUND}</td>
                <td><button className="btn-cancel" onClick={() => openLoanRepay(l)}>상환</button></td>
              </tr>
            ))}
            {loans.length === 0 && <tr><td colSpan={6} className="muted">대출 없음</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="banking-section-head">
          <h3>예금</h3>
          <button className="btn-buy" onClick={openDepositOpen}>신규 예금</button>
        </div>
        <table className="data-table">
          <thead><tr><th>코드</th><th>원금</th><th>잔액(이자포함)</th><th>연이율</th><th>시작</th><th></th></tr></thead>
          <tbody>
            {deposits.map(d => (
              <tr key={d.DEPOSIT_CODE}>
                <td>#{d.DEPOSIT_CODE}</td>
                <td className="num">₩ {d.PRINCIPAL.toLocaleString()}</td>
                <td className="num"><strong>₩ {d.BALANCE.toLocaleString()}</strong></td>
                <td>{(d.INTEREST_RATE_BPS / 100).toFixed(1)}%</td>
                <td>S{d.START_SEASON} R{d.START_ROUND}</td>
                <td><button className="btn-cancel" onClick={() => openDepositClose(d)}>해지</button></td>
              </tr>
            ))}
            {deposits.length === 0 && <tr><td colSpan={6} className="muted">예금 없음</td></tr>}
          </tbody>
        </table>
      </section>

      {modal && <AmountModal spec={modal} onClose={() => setModal(null)} />}
    </div>
  )
}

// ── Phase 9: 금액 입력 모달 (대출/예금 공통, window.prompt 대체) ──
interface ModalSpec {
  title: string; label: string; confirmLabel: string; kind: 'buy' | 'cancel'
  defaultAmount: number; maxAmount?: number; fixed?: boolean
  projection: (amount: number) => string
  onConfirm: (amount: number) => void
}
function AmountModal({ spec, onClose }: { spec: ModalSpec, onClose: () => void }) {
  const [amt, setAmt] = useState<number>(Math.max(0, Math.round(spec.defaultAmount)))
  const capped = spec.maxAmount != null ? Math.min(amt, spec.maxAmount) : amt
  const valid = capped > 0 && (spec.maxAmount == null || capped <= spec.maxAmount)

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h3>{spec.title}</h3>
        {!spec.fixed && (
          <>
            <div className="modal-row"><label>{spec.label}</label></div>
            <input className="modal-amount" type="number" value={amt} min={0} max={spec.maxAmount}
              onChange={e => setAmt(Math.max(0, Number(e.target.value) || 0))} />
            {spec.maxAmount != null && (
              <div className="modal-quick">
                <button onClick={() => setAmt(Math.round(spec.maxAmount! * 0.25))}>25%</button>
                <button onClick={() => setAmt(Math.round(spec.maxAmount! * 0.5))}>50%</button>
                <button onClick={() => setAmt(spec.maxAmount!)}>최대</button>
              </div>
            )}
          </>
        )}
        {spec.fixed && <div className="modal-row total"><label>{spec.label}</label><strong>₩ {spec.defaultAmount.toLocaleString()}</strong></div>}
        <p className="modal-projection">{spec.projection(capped)}</p>
        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose}>취소</button>
          <button className={spec.kind === 'buy' ? 'btn-buy' : 'btn-sell'} disabled={!valid && !spec.fixed}
            onClick={() => spec.onConfirm(spec.fixed ? spec.defaultAmount : capped)}>{spec.confirmLabel}</button>
        </div>
      </div>
    </div>
  )
}

// ── 신용 점수 추이 차트 (월별 CREDIT_SCORE, 등급 밴드) ──
function CreditTrendSvg({ hist }: { hist: CreditHist[] }) {
  if (hist.length === 0) return <p className="muted">월 결산이 진행되면 신용 점수 추이가 누적됩니다.</p>
  const w = 300, h = 120, pad = 22
  const loY = 200, hiY = 1000
  const yOf = (s: number) => h - pad - ((Math.max(loY, Math.min(hiY, s)) - loY) / (hiY - loY)) * (h - pad * 2)
  const n = hist.length
  const dx = n > 1 ? (w - pad * 2) / (n - 1) : 0
  const pts = hist.map((p, i) => `${pad + i * dx},${yOf(p.CREDIT_SCORE)}`).join(' ')
  const last = hist[n - 1].CREDIT_SCORE
  const first = hist[0].CREDIT_SCORE
  const up = last >= first
  const bands = [{ s: 800, g: 'AA' }, { s: 700, g: 'A' }, { s: 600, g: 'BBB' }, { s: 500, g: 'BB' }]
  return (
    <svg width={w} height={h} className="stock-chart">
      <rect x="0" y="0" width={w} height={h} fill="#102234" />
      {bands.map(b => (
        <g key={b.g}>
          <line x1={pad} y1={yOf(b.s)} x2={w - pad} y2={yOf(b.s)} stroke="#1f3a5f" strokeWidth="1" strokeDasharray="2 3" />
          <text x={w - pad + 1} y={yOf(b.s) + 3} fill="#6b7a99" fontSize="8">{b.g}</text>
        </g>
      ))}
      {n > 1 && <polyline points={pts} fill="none" stroke={up ? '#64ffda' : '#ff7043'} strokeWidth="2" />}
      <circle cx={pad + (n - 1) * dx} cy={yOf(last)} r="3" fill={up ? '#64ffda' : '#ff7043'} />
      <text x={pad} y={12} fill="#8892b0" fontSize="9">{`${hist[0].MONTH_NO}월~${hist[n - 1].MONTH_NO}월`}</text>
      <text x={w - pad} y={12} fill="#ccd6f6" fontSize="10" textAnchor="end">{last}점</text>
    </svg>
  )
}
